import { useEffect, useState } from 'react';

export interface StorageHealth {
  /** True when the browser promised not to clear this data on its own; null when unknown. */
  persisted: boolean | null;
  usedBytes: number | null;
  quotaBytes: number | null;
}

/** Ask the browser not to clear the offline data when it is short of space. */
export async function requestPersistentStorage(): Promise<boolean | null> {
  try {
    if (!navigator.storage?.persist) return null;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return null;
  }
}

export async function readStorageHealth(): Promise<StorageHealth> {
  try {
    const [persisted, est] = await Promise.all([
      navigator.storage?.persisted?.() ?? Promise.resolve(null),
      navigator.storage?.estimate?.() ?? Promise.resolve(null),
    ]);
    return {
      persisted,
      usedBytes: est?.usage ?? null,
      quotaBytes: est?.quota ?? null,
    };
  } catch {
    return { persisted: null, usedBytes: null, quotaBytes: null };
  }
}

/** What to tell the person, if anything. */
export function storageWarning(h: StorageHealth): string | null {
  if (h.usedBytes !== null && h.quotaBytes && h.usedBytes / h.quotaBytes > 0.8) {
    return 'This computer is almost out of space for LabView. Free some disk space, or reports may not be saved.';
  }
  return null;
}

export function useStorageHealth(): { warning: string | null } {
  const [warning, setWarning] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    const check = async () => {
      await requestPersistentStorage();
      const h = await readStorageHealth();
      if (alive) setWarning(storageWarning(h));
    };
    void check();
    const t = setInterval(() => void check(), 10 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);
  return { warning };
}
